import type { DataChoice, FieldViewProps, Localization } from '../../types'
import type { FilterField } from '../useFilterField'
import type TextController from './Controller'

import React, { useMemo, useRef } from 'react'
import { prop, uniq, uniqBy } from 'lodash/fp'

import {
	InputBase,
	InputBaseControl,
	InputBaseInput,
} from '@/shared/components/ui/input-base'

import Combobox from '../../components/Combobox'
import CreatebleMultiSelectField from '../../components/CreatebleMultiSelectField'
import ReferencePicker from '../../components/ReferencePicker'
import ReferenceWrapper from '../../components/ReferenceWrapper'
import { Operators } from '../../constants'
import { stringToArray, toOperationValueOption } from '../../utils/utils'
import { useFilterField } from '../useFilterField'

type TextViewProps = FieldViewProps<TextController>

export default function TextView({
	storedValue,
	field,
	invalidMessage,
	onChange,
	locale,
	disabled,
	fields,
	enableReferencing,
}: TextViewProps) {
	const { useSelectOptions } = field

	const filterField = useFilterField<TextController>(field, {
		initialState: storedValue,
		onChange,
	})

	const focusTimeoutId = useRef<any>(null)
	const operationsRef = useRef<HTMLButtonElement | null>(null)
	const inputRef = useRef<HTMLButtonElement | HTMLInputElement | null>(null)

	const selectOptions = useSelectOptions?.() || { data: [] }
	const options: DataChoice[] = useMemo(
		() => uniqBy(prop('value'), selectOptions.data || []) || [],
		[storedValue, selectOptions.data]
	)
	const operation = filterField.getFilterOperation()
	const mode = filterField.getFilterMode()

	const focusValueInput = () => {
		focusTimeoutId.current = setTimeout(() => {
			inputRef.current?.focus?.()
		}, 10)
	}

	const handleChangeOperation = (operation: Operators) => {
		filterField.setFilterOperation(operation)
		focusValueInput()
	}

	const filterTypes = field.getFilterTypes().map((t) => ({
		...t,
		label: locale.getOperatorLabel(t.operation),
	}))
	const operators = filterTypes.map(toOperationValueOption)
	const selectedTilterType = filterTypes.find((t) => t.operation === operation)!
	const isInvalid = Boolean(invalidMessage)

	return (
		<>
			<div className='flex items-center'>
				<Combobox
					locale={locale}
					data-testid='query-builder-operation-select'
					ref={operationsRef}
					name='operation'
					placeholder={locale.getOperationPlaceholder()}
					className='w-full'
					disabled={disabled}
					initialOpen={!operation}
					invalid={isInvalid}
					value={operation}
					onChange={handleChangeOperation}
					options={operators}
				/>
			</div>
			<div className='col-span-2 flex items-center'>
				{selectedTilterType?.hasInput ? (
					enableReferencing ? (
						<ReferenceWrapper filterField={filterField} locale={locale}>
							{mode === 'reference' ? (
								<ReferencePicker
									locale={locale}
									filterField={filterField}
									fields={fields}
									isInvalid={isInvalid}
								/>
							) : (
								<StringEditor
									inputRef={inputRef as React.RefObject<HTMLInputElement>}
									placeholder={locale.getEnterValuePlaceholder()}
									filterField={filterField}
									locale={locale}
									options={options}
									isLoading={selectOptions.isLoading}
									operators={operators}
									invalid={isInvalid}
								/>
							)}
						</ReferenceWrapper>
					) : (
						<StringEditor
							inputRef={inputRef as React.RefObject<HTMLInputElement>}
							placeholder={locale.getSelectValuePlaceholder()}
							filterField={filterField}
							locale={locale}
							options={options}
							isLoading={selectOptions.isLoading}
							operators={operators}
							invalid={isInvalid}
						/>
					)
				) : null}
			</div>
		</>
	)
}

const StringEditor = ({
	filterField,
	placeholder,
	invalid,
	disabled,
	locale,
	options,
	isLoading,
	inputRef,
	operators,
	...props
}: {
	inputRef: React.RefObject<HTMLInputElement>
	placeholder: string
	filterField: FilterField<TextController>
	invalid?: boolean
	disabled?: boolean
	locale: Localization
	options: DataChoice[]
	isLoading?: boolean
	operators: { label: string; value: Operators }[]
}) => {
	const operation = filterField.getFilterOperation()
	const value = filterField.getFilterValue()

	const commonProps = {
		isLoading,
		options,
		placeholder,
		invalid,
		disabled,
		locale,
	}
	const isOperationAvailable = (operation: Operators) =>
		!!operators.find((o) => o.value === operation)
	const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
		const text = e.clipboardData.getData('text/plain')
		const pastedValue = text
			.split('\n')
			.map((line) => line.trim())
			.filter((v) => v !== '')

		if (pastedValue?.length > 1 && isOperationAvailable(Operators.IN)) {
			e.preventDefault()
			filterField.setFilter(
				Operators.IN,
				uniq([...stringToArray(value as string), ...pastedValue])
			)
		}
	}

	return [Operators.IN, Operators.NOT_IN].includes(operation) ? (
		<CreatebleMultiSelectField
			ref={inputRef as unknown as React.Ref<HTMLButtonElement>}
			value={value as string[]}
			onValueChange={filterField.setFilterValue}
			{...commonProps}
			{...props}
		/>
	) : (
		<InputBase aria-invalid={invalid} className='w-full' {...props}>
			<InputBaseControl>
				<InputBaseInput
					ref={inputRef as React.Ref<HTMLInputElement>}
					data-testid='query-builder-value-string'
					value={value as string}
					onChange={(e) => filterField.setFilterValue(e.target.value)}
					onPaste={handlePaste}
					autoComplete='off'
					autoCorrect='off'
					{...commonProps}
				/>
			</InputBaseControl>
		</InputBase>
	)
}
